/**
 * Dawn v15.4.1 ProductInfo (Misell simplified version)
 * Misell theme - high-variant support
 *
 * Simplified from Dawn's original to work with Misell's jQuery gallery.
 * Key changes:
 * - Removed media gallery handling (Misell uses jQuery for this)
 * - Added variant:change event for jQuery gallery integration
 * - Simplified quantity handling
 */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      connectedCallback() {
        console.log('[ProductInfo] connectedCallback - element ready');
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        console.log('[ProductInfo] Subscribed to optionValueSelectionChange');

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedOptions } }) {
        console.log('[ProductInfo] handleOptionValueChange called');
        console.log('[ProductInfo] event.target:', event.target);
        console.log('[ProductInfo] this.contains(event.target):', this.contains(event.target));

        // Only handle events from within this product-info element
        if (!this.contains(event.target)) {
          console.log('[ProductInfo] Event target not in this product-info, ignoring');
          return;
        }

        // Get product URL from variant-selects element, target, or product-info
        const variantSelects = this.querySelector('variant-selects');
        const productUrl = variantSelects?.dataset.productUrl || target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        console.log('[ProductInfo] productUrl:', productUrl);

        if (!productUrl) {
          console.error('[ProductInfo] No product URL found');
          return;
        }

        this.pendingRequestUrl = productUrl;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, selectedOptions),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl),
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (targetId) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, selectedOptions = []) {
        const params = [];

        params.push(`section_id=${this.sectionId}`);

        const hasAllOptionValueIds = optionValues.length && optionValues.every(Boolean);

        if (hasAllOptionValueIds) {
          params.push(`option_values=${optionValues.join(',')}`);
        } else if (selectedOptions.length) {
          // Legacy fallback: send option1/2/3 params to resolve variant server-side
          selectedOptions.forEach((opt) => {
            if (opt.position) {
              params.push(`option${opt.position}=${encodeURIComponent(opt.value)}`);
            }
          });
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          console.log('[ProductInfo] handleUpdateProductInfo - variant:', variant);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            console.log('[ProductInfo] No variant found, setting unavailable');
            this.setUnavailable();
            return;
          }

          // Update price, SKU, inventory from fetched HTML
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');

          // Update submit button state
          const submitButton = this.querySelector('[data-js-product-button-add-to-cart]');
          const newSubmitButton = html.querySelector('[data-js-product-button-add-to-cart]');
          if (submitButton && newSubmitButton) {
            submitButton.disabled = newSubmitButton.disabled;
            submitButton.innerHTML = newSubmitButton.innerHTML;
          }

          // Publish variant change event (Dawn's internal event)
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Dispatch DOM event for MISEる jQuery gallery integration
          this.dispatchEvent(new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant, variantId: variant.id }
          }));
        };
      }

      updateVariantInputs(variantId) {
        console.log('[ProductInfo] updateVariantInputs called with:', variantId);

        // Priority 1: Update Dawn hidden input (supports 2048 variants)
        const dawnInput = this.querySelector('input[data-dawn-variant-id]');
        if (dawnInput) {
          console.log('[ProductInfo] Updating Dawn hidden input');
          dawnInput.value = variantId ?? '';
          dawnInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Priority 2: Update all hidden inputs with name="id"
        this.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Priority 3: Try to update select (may fail for variants > 250)
        this.querySelectorAll('select[name="id"]').forEach((select) => {
          // Only set if option exists
          const optionExists = Array.from(select.options).some(opt => opt.value == variantId);
          if (optionExists) {
            select.value = variantId ?? '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const submitButton = this.querySelector('[data-js-product-button-add-to-cart]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = window.variantStrings?.unavailable || 'Unavailable';
        }

        const selectors = ['price', 'Inventory', 'Sku']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
